import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { financeApi } from '@/api/finance.api';
import type { FinanceIntegrationHealth } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

const STATUS_COLOR: Record<string, string> = {
  HEALTHY: 'text-green-700',
  WARNING: 'text-amber-700',
  CRITICAL: 'text-red-700',
};

export function FinanceIntegrationsPage() {
  const [health, setHealth] = useState<FinanceIntegrationHealth | null>(null);

  useEffect(() => {
    financeApi.integrationHealth().then(setHealth).catch((e) => showApiError(e, 'Failed to load integration health'));
  }, []);

  return (
    <div>
      <PageHeader
        title="Integration health"
        description="Accounting events, approvals, and bank reconciliation status across connected modules"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Integrations' },
        ]}
      />

      {health && (
        <>
          <p className={`mb-4 text-sm font-medium ${STATUS_COLOR[health.overallStatus] ?? ''}`}>
            Overall status: {health.overallStatus}
          </p>
          <KpiGrid className="mb-6">
            <StatCard title="Pending accounting events" value={health.pendingAccountingEvents} />
            <StatCard title="Pending approvals" value={health.pendingApprovals} />
            <StatCard title="Unreconciled bank txns" value={health.unreconciledBankTransactions} />
            <StatCard title="Failed automations (24h)" value={health.failedAutomationRuns24h} />
          </KpiGrid>
          <div className="card p-4">
            <h3 className="font-semibold mb-2">Connected modules</h3>
            <p className="text-sm text-slate-600">{health.integrationModules.join(' · ')}</p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link to="/finance/ledger" className="text-primary-600 hover:underline">
                Accounting events →
              </Link>
              <Link to="/finance/workflows" className="text-primary-600 hover:underline">
                Approvals →
              </Link>
              <Link to="/finance/banking" className="text-primary-600 hover:underline">
                Bank reconciliation →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
